import {Injectable, signal} from '@angular/core';
import {BcGqlService, IGQLChainShort, IGQLResponse, IGQLUserPosition} from '@betterchains/bc-gql';
import {first} from 'rxjs';
import {HttpResponse} from '@angular/common/http';

export interface ILoadedBranch {
  id: number,
  name: string,
  archived: boolean
}

export interface ILoadedUser {
  id: number,
  email: string,
  first_name: string,
  last_name: string,
  full_name: string
}

@Injectable({
  providedIn: 'root'
})
export class SystemService {

  public chains: IGQLChainShort[] = []
  private cachedBranches: { [index: number]: ILoadedBranch[] } = {}
  private cachedManagers: { [index: number]: ILoadedUser[] } = {}
  private cachedUsers: { [index: number]: ILoadedUser[] } = {}
  private cachedPositions: { [index: number]: IGQLUserPosition[] } = {}

  constructor(
    private gql: BcGqlService,
  ) {
  }

  private loadChains() {
    this.gql.query(
      // language=gql
      `
        query chains {
          system {
            chains(limit: -1) {
              chain_id,
              name,
            }
          }
        }
      `
    ).pipe(first()).subscribe((r: HttpResponse<IGQLResponse<any>>) => {
      this.chains = r.body.data.system.chains;
    })
  }

  branchesLoading = signal(false)

  public GetBranches(chainId: number) {
    return new Promise<ILoadedBranch[]>((resolve, reject) => {
      if(chainId in this.cachedBranches) {
        resolve(this.cachedBranches[chainId])
        return;
      }
      this.branchesLoading.set(true);
      this.gql.query(
        // language=gql
        `query GetBranchesByChainId($chainId: Int) {
          system {
            branches(chain_id: $chainId) {
              branch_id,
              name,
              archived
            }
          }
        }
        `,
        {chainId}
      ).pipe(first()).subscribe({
        next: (r: HttpResponse<IGQLResponse<{ system: { branches: ILoadedBranch[] } }>>) => {
          this.cachedBranches[chainId] = r.body.data.system.branches.map((b: any) => {
            return {
              id: b.branch_id,
              name: b.archived ? b.name + " - INACTIVE" : b.name,
              archived: b.archived,
            }
          });
          resolve(this.cachedBranches[chainId])
          this.branchesLoading.set(false);
        },
        error: () => {
          this.branchesLoading.set(false);
          reject()
        }
      })
    })
  }

  public GetManagers(chainId: number) {
    return new Promise<ILoadedUser[]>((resolve, reject) => {
      if (chainId in this.cachedManagers) {
        resolve(this.cachedManagers[chainId])
      } else {
        this.loadUsers(chainId, ["chain_manager", "branch_manager", "district_manager"])
          .then(r => {
            this.cachedManagers[chainId] = r
            resolve(r)
          })
          .catch(reject)
      }
    })
  }

  public GetUsers(chainId: number) {
    return new Promise<ILoadedUser[]>((resolve, reject) => {
      if (chainId in this.cachedUsers) {
        resolve(this.cachedUsers[chainId])
      } else {
        this.loadUsers(chainId)
          .then(r => {
            this.cachedUsers[chainId] = r
            resolve(r)
          })
          .catch(reject)
      }
    })
  }

  public GetPositions(chainId: number) {
    return new Promise<IGQLUserPosition[]>((resolve, reject) => {
      if (chainId in this.cachedPositions) {
        resolve(this.cachedPositions[chainId])
      } else {
        this.gql.query(
          // language=gql
          `
            query GetChainPositions($chainId: String) {
              hr {
                positions(filter: [{column: "chain_id", value: $chainId, type: eq}]) {
                  id
                  name
                }
              }
            }
          `,{chainId: chainId.toString()}
        ).pipe(first()).subscribe({
          next: (r: HttpResponse<IGQLResponse<{ hr: { positions: IGQLUserPosition[] } }>>) => {
            this.cachedPositions[chainId] = r.body.data.hr.positions
            resolve(this.cachedPositions[chainId])
          },
          error: () => {
            reject()
          }
        })
      }
    })
  }

  private loadUsers(chainId: number, roles: string[] = []) {
    return new Promise<ILoadedUser[]>((resolve, reject) => {
      this.gql.query(
        // language=gql
        `
          query GetChainManagers($chainId: String, $roles: [String!]) {
            hr {
              employees(
                limit: 0
                account_status: 6,
                acl_roles: $roles
                filter: [{column: "user.chain_id", value: $chainId, type: eq}]
              ) {
                id
                first_name
                last_name
              }
            }
          }
        `, {chainId: chainId.toString(), roles}
      ).pipe(first()).subscribe({
        next: (r: HttpResponse<IGQLResponse<{ hr: { employees: ILoadedUser[], positions: any[] } }>>) => {
          this.cachedManagers[chainId] = r.body.data.hr.employees.map(e => {
            e.full_name = e.first_name + ' ' + e.last_name
            return e
          })
          resolve(this.cachedManagers[chainId])
        },
        error: () => {
          reject()
        }
      })
    })
  }

  Init() {
    this.loadChains();
  }
}
